import React, { useMemo, useEffect, useState } from "react"

import { Container, CardBody, Card } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"

import moment from "moment"

import { axiosbaseurl } from "configure"
import Loader from "components/Common/Loader"
import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import AddHoursTime from "./AddHoursTime"
import AddReporteesManualLogs from "./AddReporteesManualLogs"
import { setUpdatedData } from "helpers/data_setters"
import { deleteNow } from "helpers/delete_function"
import TimesheetFilter from "components/Common/TimesheetFilter"
import CommonTable from "components/Common/CommonTable"
import secureLocalStorage from "react-secure-storage"

const AllTimeSheets = ({ isReportees }) => {
  const [table_data, settable_data] = useState([])
  const [loading, setLoading] = useState(false)
  const [all_logs, setAllLogs] = useState([])
  const [emp_options, setemp_options] = useState([])
  const [status_options, setstatus_options] = useState([])
  const [all_services, setAllServices] = useState([])

  const loggedin_user = secureLocalStorage.getItem("authUser")

  const getLogs = async () => {
    try {
      setLoading(true)
      const response = await axiosbaseurl.get(`/task-updates/timesheet/logs`)

      if (response.data.success) {
        const logsdata = response.data.data
          .map(item => ({
            ...item,
            employeeName:
              item?.employee?.name + " " + item?.employee?.lastName || "",
            timeSpent:
              calculateTimeDifference(item.startTime, item.endTime) + " hrs",
            statusName: item?.status?.statusName,
            serviceName: {
              id: item?.task?.serviceName?.id,
              serviceName: item?.serviceName,
            },
            serviceNameStr: item?.serviceName || "N/A",
            clientName: item?.clientName || item?.client?.clientName || "N/A",
          }))
          ?.sort((a, b) => a.id - b.id)

        let finalLogs = logsdata

        if (loggedin_user.id !== 1) {
          finalLogs = isReportees
            ? logsdata?.filter(
                log => log.employeeId != loggedin_user?.employeeId
              )
            : logsdata?.filter(
                log => log.employeeId == loggedin_user?.employeeId
              )
        }

        // console.log(finalLogs)

        settable_data(finalLogs)
        setAllLogs(finalLogs)

        const emps = []
        finalLogs
          .filter(item => item.employee)
          .forEach(curr => {
            if (!emps.some(i => i.id === curr.employee.id))
              emps.push(curr.employee)
          })
        setemp_options(emps)

        // making service oprtions
        const uniqueService = [
          ...new Map(
            finalLogs
              ?.map(t => t?.task?.serviceName)
              ?.filter(Boolean) // removes null / undefined
              ?.map(sts => [sts.id, sts])
          ).values(),
        ]
        setAllServices(uniqueService)

        // making status oprtions
        const uniqueStatus = [
          ...new Map(
            finalLogs
              ?.map(t => t.status)
              ?.filter(Boolean) // removes null / undefined
              ?.map(sts => [sts.id, sts])
          ).values(),
        ]
        setstatus_options(uniqueStatus)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getLogs()
  }, [isReportees])

  function calculateTimeDifference(startTime, endTime) {
    // Parse the start and end times as moment objects
    const start = moment(startTime, "HH:mm")
    const end = moment(endTime, "HH:mm")

    // If end time is earlier than start time, add a day to end time
    if (end.isBefore(start)) {
      end.add(1, "days")
    }

    // Calculate the difference in minutes
    const duration = moment.duration(end.diff(start))

    // Get total hours and minutes
    const hours = String(Math.floor(duration.asHours())).padStart(2, "0") // Ensure two digits
    const minutes = String(duration.minutes()).padStart(2, "0") // Ensure two digits

    return `${hours}:${minutes}`
  }

  document.title = "Timesheet Management | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}

      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb
            title={"Dashboard"}
            breadcrumbItem={
              isReportees ? "Reportees Timesheet" : "My Timesheet"
            }
          />

          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                table_data={table_data}
                settable_data={settable_data}
                all_logs={all_logs}
                setAllLogs={setAllLogs}
                emp_options={emp_options}
                calculateTimeDifference={calculateTimeDifference}
                status_options={status_options}
                all_services={all_services}
                isReportees={isReportees}
                refreshLogs={getLogs}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({
  table_data,
  settable_data,
  all_logs,
  setAllLogs,
  emp_options,
  calculateTimeDifference,
  status_options,
  all_services,
  isReportees,
  refreshLogs,
}) => {
  const [editShow, setEditShow] = useState(false)
  const [addShow, setAddShow] = useState(false)
  const [ids, setIds] = useState({})
  const [isRetroAllowed, setIsRetroAllowed] = useState(false)

  const { checkAccess, permissions } = usePermissionData()
  const module_id = 24

  const navigate = useNavigate()

  const loggedin_user = secureLocalStorage.getItem("authUser")

  useEffect(() => {
    const checkRetroAccess = async () => {
      try {
        // Super admin never sees it - retrospective is employee-only
        if (loggedin_user?.id === 1) {
          setIsRetroAllowed(false)
          return
        }

        // Instant value if already present on login payload (avoids flicker)
        const cached = loggedin_user?.isRetroTimesheetAllowed
        if (
          cached === true ||
          cached === 1 ||
          String(cached).toLowerCase() === "true" ||
          String(cached) === "1"
        ) {
          setIsRetroAllowed(true)
        }

        // No employee record to check -> stay hidden (unless cached above)
        if (!loggedin_user?.employeeId) {
          return
        }

        const response = await axiosbaseurl.get(
          `/employee-master/${loggedin_user.employeeId}`
        )
        const val = response?.data?.data?.isRetroTimesheetAllowed
        setIsRetroAllowed(
          val === true ||
            val === 1 ||
            String(val).toLowerCase() === "true" ||
            String(val) === "1"
        )
      } catch (error) {
        console.log(error)
      }
    }

    checkRetroAccess()
  }, [])

  function isEditable(date) {
    return moment(date).format("DD-MM-YYYY") === moment().format("DD-MM-YYYY")
  }

  const handleDelete = async id => {
    try {
      const response = await axiosbaseurl.delete(`/task-updates/${id}`)
      if (response.data.success) {
        settable_data(prev =>
          setUpdatedData(prev, response.data.data, "delete")
        )
        setAllLogs(prev => setUpdatedData(prev, response.data.data, "delete"))
      }
    } catch (error) {
      console.log(error)
      swal("", error.response.data.message || "Something went Wrong", "error")
    }
  }

  // columns
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "ID",
        filterable: true,
        disableFilters: true,
        className: "  text-center",
        Cell: cellProps => <span>{cellProps.row.index + 1}</span>,
      },
      {
        Header: "Client",
        accessor: "clientName",
        disableFilters: true,
        // className: "text-center",
        filterable: true,
        // Cell: cellProps => (
        //   <span>{cellProps.row.original?.client?.clientName}</span>
        // ),
      },
      {
        Header: "Employee",
        accessor: "employeeName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Time Spent",
        accessor: "timeSpent",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Task Name",
        accessor: "task.title",
        disableFilters: true,
        filterable: true,
        className: "d-none",
      },
      {
        Header: "Service",
        accessor: "serviceNameStr",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Period End",
        accessor: "periodEnd",
        disableFilters: true,
        filterable: true,
        className: "text-center",
        Cell: cellProps => (
          <span>
            {cellProps.row.original.periodEnd
              ? moment(cellProps.row.original.periodEnd).format("DD/MM/YYYY")
              : "N/A"}
          </span>
        ),
      },
      {
        Header: "Due Date",
        accessor: "dueDate",
        disableFilters: true,
        filterable: true,
        className: "text-center",
        Cell: cellProps => (
          <span>
            {cellProps.row.original.dueDate
              ? moment(cellProps.row.original.dueDate).format("DD/MM/YYYY")
              : "N/A"}
          </span>
        ),
      },

      {
        Header: "Status",
        disableSort: true,
        accessor: "statusName",
        disableFilters: true,
        filterable: true,
        className: "text-center",
        Cell: cellProps => {
          if (cellProps.row.original?.status) {
            let { colorCode, statusName } = cellProps.row.original?.status
            return (
              <div className="text-center">
                <span
                  className="badge fs-6"
                  style={{
                    color: colorCode,
                    border: `1px solid ${colorCode}`,
                    fontSize: "11px",
                  }}
                >
                  {statusName}{" "}
                </span>
              </div>
            )
          }
          return <>N/A</>
        },
      },

      ...(isReportees
        ? [
            {
              Header: "Submitted On",
              accessor: "createdAt",
              disableFilters: true,
              filterable: true,
              Cell: cellProps => (
                <span>
                  {cellProps.row.original.createdAt
                    ? moment(cellProps.row.original.createdAt).format(
                        "DD/MM/YY • HH:mm"
                      )
                    : "N/A"}
                </span>
              ),
            },
          ]
        : []),

      {
        Header: "Action",
        disableFilters: true,
        disableSort: true,
        accessor: "view",
        className: "col-1 text-end",
        Cell: cellProps => {
          return (
            <div>
              {/* edit */}
              {checkAccess(module_id, "edit") &&
              isEditable(cellProps.row.original.createdAt) ? (
                <button
                  onClick={() => {
                    setEditShow(true)
                    setIds({
                      ...cellProps.row.original,
                      logId: cellProps.row.original.id,
                      adminEdit: true,
                    })
                  }}
                  className="btn btn-success bg-green  btn-sm me-2"
                  type="button"
                >
                  <i className="fas fa-pen"></i>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setEditShow(true)
                    setIds({
                      ...cellProps.row.original,
                      logId: cellProps.row.original.id,
                      adminView: true,
                    })
                  }}
                  className="btn btn-primary btn-sm me-2"
                  title="view Edited Reason"
                >
                  <i className="fas fa-eye"></i>
                </button>
              )}

              {checkAccess(module_id, "remove") && (
                <button
                  onClick={() =>
                    deleteNow(() => handleDelete(cellProps.row.original.id))
                  }
                  className="btn btn-danger btn-sm "
                  title="Delete"
                >
                  <i className="fas fa-trash"></i>
                </button>
              )}
            </div>
          )
        },
      },
    ],
    [permissions, isReportees]
  )

  // table data
  const data = useMemo(() => {
    return table_data
  }, [table_data])

  return (
    <>
      <PermittedComponent isAccess={checkAccess(module_id, "view")}>
        <CommonTable columns={columns} data={data} heading={"Task Logs"}>
          <div>
            {isRetroAllowed && (
              <button
                onClick={() => setAddShow(true)}
                className="btn btn-primary bg-primary ms-3"
              >
                <i className="fas fa-plus me-3"></i>
                Retrospective Timesheet
              </button>
            )}
            <TimesheetFilter
              data={{
                status_options,
                table_data,
                settable_data,
                all_tasks: all_logs,
                all_services,
                emp_options,
                calculateTimeDifference,
                type: "timesheet",
              }}
            />

            <button
              onClick={() => navigate(-1)}
              className="btn btn-secondary bg-dark ms-3"
            >
              <i className="fas fa-arrow-left me-3"></i>
              Back
            </button>
          </div>
        </CommonTable>

        <AddHoursTime
          show={editShow}
          setShow={setEditShow}
          ids={ids}
          setLogsData={settable_data}
        />

        <AddReporteesManualLogs
          show={addShow}
          setShow={setAddShow}
          hideTrigger={true}
          onSuccess={() => {
            if (refreshLogs) refreshLogs()
          }}
        />
      </PermittedComponent>
    </>
  )
}

export default AllTimeSheets
